import React, { useEffect, useRef, useState } from 'react'

// Keeps a locally-edited draft in sync with an upstream source, without
// clobbering unsaved edits when the source changes underneath (e.g. an SWR
// background revalidation, or another admin's change landing). Extracted so
// the resync-while-dirty behaviour can be tested directly with `renderHook`,
// independent of data fetching and timing.
export const useResyncingDraft = <Source, D>(
  source: Source | undefined,
  toDraftFn: (s: Source) => D,
): [D | null, React.Dispatch<React.SetStateAction<D | null>>, boolean] => {
  const [draft, setDraft] = useState<D | null>(null)

  // Whether the draft had unsaved edits as of the END of the previous
  // render (see the tracking effect below) — read by the resync effect so
  // it can stay [source]-only (exhaustive-deps clean) while still skipping
  // the resync when there are unsaved edits. This must reflect the *prior*
  // render's dirtiness, not the current one: the current render's `dirty`
  // is computed against whatever `source` just changed to, which is
  // exactly the value a legitimate resync needs to apply, so gating on it
  // directly would permanently block resyncs.
  const dirtyRef = useRef(false)

  useEffect(() => {
    // A background revalidation must not clobber an in-progress edit. Skip
    // the resync while dirty; it re-applies once the edit is saved (dirty
    // becomes false and `source` updates to match) or discarded.
    if (dirtyRef.current) return
    if (source !== undefined) setDraft(toDraftFn(source))
    // `toDraftFn` is expected to be a stable reference (a module-level pure
    // function at every call site in this file); depending on `source` only
    // keeps the dirty-guard comment above accurate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source])

  const dirty =
    draft !== null &&
    source !== undefined &&
    JSON.stringify(draft) !== JSON.stringify(toDraftFn(source))

  // Runs after every render (deliberately no dependency array), so it
  // records this render's dirtiness for the *next* commit — after the
  // resync effect above has already acted on the previous value.
  useEffect(() => {
    dirtyRef.current = dirty
  })

  return [draft, setDraft, dirty]
}
